import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Typography,
	Row,
	Col,
	Space,
	notification,
	Divider,
	Table,
	message,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PiUsersDuotone, PiArrowLeftDuotone, PiPlusDuotone, PiTrashDuotone } from "react-icons/pi";
import { requestCreateTeam, requestGetUserList } from "../../services/admin.service";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

function AddTeamPage() {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [usersLoading, setUsersLoading] = useState(false);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [teamMembers, setTeamMembers] = useState([]);

	useEffect(() => {
		fetchUsers();
	}, []);

	const fetchUsers = () => {
		setUsersLoading(true);
		requestGetUserList({ limit: 1000 }) // Get all users for selection
			.then((response) => {
				const users = response.data.data || response.data;
				setAvailableUsers(users);
			})
			.catch((error) => {
				console.error("Error fetching users:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch users for team member selection",
				});
			})
			.finally(() => {
				setUsersLoading(false);
			});
	};

	const handleSubmit = (values) => {
		setLoading(true);

		// Convert team members to the proper schema format
		const members = teamMembers.map((member) => ({
			userId: member.userId,
			role: member.role || "MEMBER",
		}));

		const teamData = {
			name: values.name,
			description: values.description || "",
			currency: values.currency,
			ownerId: values.ownerId,
			members: members,
			company: {
				legalName: values.legalName || "",
				address: values.address || "",
				country: values.country || "",
				vatNumber: values.vatNumber || "",
			},
			metadata: {},
		};

		requestCreateTeam(teamData)
			.then((response) => {
				notification.success({
					message: "Success",
					description: "Team created successfully",
				});
				navigate("/teams");
			})
			.catch((error) => {
				console.error("Error creating team:", error);
				if (error.response?.data?.message) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				} else {
					notification.error({
						message: "Error",
						description: "Failed to create team. Please try again.",
					});
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const addTeamMember = () => {
		const newMember = {
			key: Date.now(), // Temporary key for table
			userId: "",
			role: "MEMBER",
		};
		setTeamMembers([...teamMembers, newMember]);
	};

	const removeTeamMember = (key) => {
		setTeamMembers(teamMembers.filter((member) => member.key !== key));
	};

	const updateTeamMember = (key, field, value) => {
		setTeamMembers(
			teamMembers.map((member) => (member.key === key ? { ...member, [field]: value } : member))
		);
	};

	// const handleMembersChange = (targetKeys) => {
	// 	setSelectedMembers(targetKeys);
	// };

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/teams")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Add Team
					</Title>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Card
					title={
						<Space>
							<PiUsersDuotone size={20} />
							<span>Team Information</span>
						</Space>
					}>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						initialValues={{
							currency: "IDR",
						}}>
						<Row gutter={24}>
							<Col span={12}>
								<Form.Item
									label="Team Name"
									name="name"
									rules={[
										{
											required: true,
											message: "Please enter team name",
										},
									]}>
									<Input placeholder="Enter team name" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Currency"
									name="currency"
									rules={[
										{
											required: true,
											message: "Please select currency",
										},
									]}>
									<Select placeholder="Select currency">
										<Option value="IDR">Indonesian Rupiah (IDR)</Option>
										<Option value="USD">US Dollar (USD)</Option>
										<Option value="EUR">Euro (EUR)</Option>
										<Option value="MYR">Malaysian Ringgit (MYR)</Option>
										<Option value="SGD">Singapore Dollar (SGD)</Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={24}>
								<Form.Item label="Description" name="description">
									<TextArea rows={3} placeholder="Enter team description (optional)" />
								</Form.Item>
							</Col>
						</Row>

						<Divider orientation="left" orientationMargin={0} plain>
							<strong>Company Information</strong>
						</Divider>

						<Row gutter={24}>
							<Col span={12}>
								<Form.Item label="Legal Name" name="legalName">
									<Input placeholder="Enter company legal name (optional)" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item label="VAT Number" name="vatNumber">
									<Input placeholder="Enter VAT number (optional)" />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={12}>
								<Form.Item label="Country" name="country">
									<Input placeholder="Enter country (optional)" />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item label="Address" name="address">
									<Input placeholder="Enter company address (optional)" />
								</Form.Item>
							</Col>
						</Row>

						<Divider orientation="left" orientationMargin={0} plain>
							<strong>Team Owner</strong>
						</Divider>

						<Row gutter={24}>
							<Col span={12}>
								<Form.Item
									label="Team Owner"
									name="ownerId"
									rules={[
										{
											required: true,
											message: "Please select team owner",
										},
									]}>
									<Select
										placeholder="Select team owner"
										loading={usersLoading}
										showSearch
										filterOption={(input, option) =>
											option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
										}>
										{availableUsers.map((user) => (
											<Option key={user._id} value={user._id}>
												{user.name} ({user.email})
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Divider orientation="left" orientationMargin={0} plain>
							<strong>Team Members</strong>
						</Divider>

						<Row gutter={24}>
							<Col span={24}>
								<Form.Item label="Team Members">
									<div style={{ marginBottom: 16 }}>
										<Button type="dashed" onClick={addTeamMember} icon={<PiPlusDuotone />} block>
											Add Team Member
										</Button>
									</div>
									<Table
										dataSource={teamMembers}
										pagination={false}
										size="small"
										rowKey="key"
										locale={{ emptyText: "No team members added yet" }}
										columns={[
											{
												title: "User",
												dataIndex: "userId",
												key: "userId",
												render: (value, record) => (
													<Select
														placeholder="Select user"
														value={value}
														onChange={(val) => updateTeamMember(record.key, "userId", val)}
														style={{ width: "100%" }}
														showSearch
														filterOption={(input, option) =>
															option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
														}>
														{availableUsers.map((user) => (
															<Option key={user._id} value={user._id}>
																{user.name} ({user.email})
															</Option>
														))}
													</Select>
												),
											},
											{
												title: "Role",
												dataIndex: "role",
												key: "role",
												width: 150,
												render: (value, record) => (
													<Select
														value={value}
														onChange={(val) => updateTeamMember(record.key, "role", val)}
														style={{ width: "100%" }}>
														<Option value="MEMBER">Member</Option>
														<Option value="ADMIN">Admin</Option>
														<Option value="VIEWER">Viewer</Option>
													</Select>
												),
											},
											{
												title: "Action",
												key: "action",
												width: 80,
												render: (_, record) => (
													<Button
														type="text"
														danger
														size="small"
														icon={<PiTrashDuotone />}
														onClick={() => removeTeamMember(record.key)}
													/>
												),
											},
										]}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Form.Item style={{ marginTop: 32 }}>
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									loading={loading}
									icon={<PiUsersDuotone />}>
									Create Team
								</Button>
								<Button onClick={() => navigate("/teams")}>Cancel</Button>
							</Space>
						</Form.Item>
					</Form>
				</Card>
			</div>
		</div>
	);
}

export default AddTeamPage;
